import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { MAIN_SITE_URL } from '@/mocks/categories';

export default function ArticlePage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const intro = t('article.intro', { returnObjects: true }) as unknown as string[];
  const safetyList = t('article.safety.list', { returnObjects: true }) as unknown as string[];
  const faqs = t('article.faq.items', { returnObjects: true }) as unknown as { q: string; a: string }[];
  const compHeaders = t('article.comparison.headers', { returnObjects: true }) as unknown as string[];
  const compRows = t('article.comparison.rows', { returnObjects: true }) as unknown as string[][];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('article.h1'),
    description: t('article.metaDescription'),
    dateModified: '2026-08-22',
    author: { '@type': 'Organization', name: t('brand') },
    publisher: { '@type': 'Organization', name: t('brand') },
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="bg-cream">
          <div className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-16">
            {/* Breadcrumbs */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-stone-500">
                <li>
                  <Link to="/" className="transition-colors hover:text-emerald-700">
                    {t('article.breadcrumbHome')}
                  </Link>
                </li>
                <li className="text-stone-300">/</li>
                <li className="text-stone-700">{t('article.breadcrumbCurrent')}</li>
              </ol>
            </nav>

            <h1 className="font-display text-2xl font-bold leading-tight text-stone-900 md:text-4xl">
              {t('article.h1')}
            </h1>
            <p className="mt-4 text-sm text-stone-500">{t('article.updated')}</p>

            <div className="mt-8 space-y-5">
              {intro.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-stone-700">
                  {p}
                </p>
              ))}
            </div>

            {/* What is */}
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.whatIs.h2')}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-stone-700">{t('article.whatIs.p1')}</p>
              <p className="mt-4 text-base leading-relaxed text-stone-700">{t('article.whatIs.p2')}</p>
            </section>

            {/* How it works */}
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.howWorks.h2')}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-stone-700">{t('article.howWorks.p1')}</p>
              <p className="mt-4 text-base leading-relaxed text-stone-700">{t('article.howWorks.p2')}</p>
              <p className="mt-4 text-base leading-relaxed text-stone-700">{t('article.howWorks.p3')}</p>
            </section>

            {/* Creator search */}
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.creatorSearch.h2')}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-stone-700">
                {t('article.creatorSearch.p1')}
              </p>
              <p className="mt-4 text-base leading-relaxed text-stone-700">
                {t('article.creatorSearch.p2')}
              </p>
            </section>

            {/* Creator links */}
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.creatorLinks.h2')}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-stone-700">
                {t('article.creatorLinks.p1')}
              </p>
              <p className="mt-4 text-base leading-relaxed text-stone-700">
                {t('article.creatorLinks.p2')}
              </p>
            </section>

            {/* Comparison table */}
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.comparison.h2')}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-stone-700">
                {t('article.comparison.p1')}
              </p>
              <div className="mt-6 overflow-hidden rounded-lg border border-stone-200 bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
                        {compHeaders.map((h) => (
                          <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {compRows.map((row, i) => (
                        <tr key={i} className="border-t border-stone-100">
                          {row.map((cell, j) => (
                            <td
                              key={j}
                              className={`whitespace-nowrap px-4 py-3 ${
                                j === 0 ? 'font-medium text-stone-900' : 'text-stone-600'
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Safety */}
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.safety.h2')}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-stone-700">{t('article.safety.p1')}</p>
              <ul className="mt-4 space-y-2.5">
                {safetyList.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-stone-700">
                    <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-base leading-relaxed text-stone-700">{t('article.safety.p2')}</p>
            </section>

            {/* FAQ */}
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.faq.h2')}
              </h2>
              <div className="mt-6 space-y-3">
                {faqs.map((faq, i) => {
                  const isOpen = openFaq === i;
                  return (
                    <div key={faq.q} className="overflow-hidden rounded-lg border border-stone-200 bg-white">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : i)}
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      >
                        <span className="text-sm font-semibold text-stone-900 md:text-base">
                          {faq.q}
                        </span>
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-stone-500">
                          <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-lg`}></i>
                        </span>
                      </button>
                      {isOpen && (
                        <div className="border-t border-stone-100 px-5 py-4">
                          <p className="text-sm leading-relaxed text-stone-600 md:text-base">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Related resources */}
            <section className="mt-12 rounded-lg border border-stone-100 bg-white p-6 md:p-8">
              <h2 className="font-display text-xl font-bold text-stone-900 md:text-2xl">
                {t('article.related.h2')}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-stone-600 md:text-base">
                {t('article.related.copy')}
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  {t('article.related.backHome')}
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-stone-200 px-6 py-3 text-sm font-semibold text-stone-800 transition-colors hover:bg-stone-50"
                >
                  {t('article.related.cta')}
                  <i className="ri-arrow-right-up-line ml-2"></i>
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
    </div>
  );
}